import React, { useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { createFAQSchema } from '../utils/structuredData';
export const FaqPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const faqSections = [{
    title: 'Getting Started',
    questions: [{
      question: 'What ages is Story Bunny best for?',
      answer: 'Story Bunny is designed for children ages 3 to 6 — but anyone can enjoy using it.'
    }, {
      question: 'Does my child need to know how to read or write?',
      answer: 'No. The interface is simple and intuitive. With just a few taps, your child can create a story from start to finish.'
    }, {
      question: 'Can kids use it on their own?',
      answer: 'Yes. Story Bunny was made to be easy enough for kids to use independently — or as something fun to do together.'
    }, {
      question: 'What devices does it work on?',
      answer: 'Story Bunny runs in your browser on any device — phone, tablet, or computer. No app or installation required.'
    }]
  }, {
    title: 'AI & Technology',
    questions: [{
      question: 'Does Story Bunny use AI?',
      answer: "Yes. Story Bunny uses AI to generate the words and images in each story, based on your child's choices."
    }, {
      question: 'Is my child chatting with AI?',
      answer: "No. Story Bunny doesn't use chat or conversation features."
    }, {
      question: 'How are the illustrations made?',
      answer: 'The artwork is created using generative AI — each image is unique and matched to the story your child creates.'
    }]
  }, {
    title: 'Account & Privacy',
    questions: [{
      question: 'Do I need to create an account?',
      answer: "Yes — you'll need to create a free account to read your first story."
    }, {
      question: 'Will the story be saved?',
      answer: "When you're logged in, your story is automatically saved as you go."
    }, {
      question: 'What data do you collect?',
      answer: "We don't collect any personal data unless you create an account. We do collect anonymous usage data to help us improve the experience."
    }]
  }, {
    title: 'Story Creation',
    questions: [{
      question: 'Are the stories appropriate for all ages?',
      answer: 'Yes. Story Bunny is designed with young children in mind, and stories are always age-appropriate and child-friendly.'
    }, {
      question: 'Can I choose what kind of story it makes?',
      answer: 'Yes — you can choose the characters, setting, art style, and even describe what happens.'
    }, {
      question: 'Can I print or download a story?',
      answer: 'Not at this time.'
    }]
  }, {
    title: 'Technical',
    questions: [{
      question: 'Is this an app?',
      answer: 'No — Story Bunny is browser-based. Just open it and start creating.'
    }, {
      question: 'Can I lock the screen while my child is using it?',
      answer: <>
              Yes —{' '}
              <Link to="/lock-screen" className="text-[#D4A088] hover:underline" data-id="element-1333">
                click here to view our step-by-step instructions
              </Link>{' '}
            </>
    }]
  }, {
    title: 'Pricing',
    questions: [{
      question: 'How much does Story Bunny cost?',
      answer: "It's $4 for 5 story credits (less than $1 per book). Each credit allows you to create one unique story that's saved to your library forever."
    }, {
      question: 'Can I use Story Bunny without purchasing?',
      answer: 'Yes — you can create your first story for free. After that, you can buy story credit packs as needed.'
    }]
  }];
  // Flatten all FAQs for structured data
  const allFAQs = faqSections.flatMap(section => section.questions.map(q => ({
    question: q.question,
    answer: typeof q.answer === 'string' ? q.answer : q.question // Handle JSX answers
  })));
  const faqStructuredData = createFAQSchema(allFAQs);
  const filteredSections = faqSections.map(section => ({
    ...section,
    questions: section.questions.filter(q => q.question.toLowerCase().includes(searchTerm.toLowerCase()) || q.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  })).filter(section => section.questions.length > 0);
  return <>
      <SEOHead title="FAQ - Frequently Asked Questions" description="Get answers to common questions about Story Bunny. Learn about our AI-powered storytelling platform, pricing, safety features, and how to create magical stories for children." canonical="https://storybunny.ai/faq" structuredData={faqStructuredData} data-id="element-1334" />
      <div className="flex-1 flex flex-col bg-[#FFFBF6] overflow-hidden" data-id="element-1335">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-[#E5C1A9] p-4" data-id="element-1336">
          <div className="max-w-7xl mx-auto" data-id="element-1337">
            <h1 className="text-2xl font-bold text-[#6B4B3E]" data-id="element-1338">FAQ</h1>
            <p className="text-sm text-[#6B4B3E]/75" data-id="element-1339">
              Frequently Asked Questions
            </p>
          </div>
        </div>
        {/* Search Bar */}
        <div className="max-w-3xl mx-auto w-full px-4 py-8" data-id="element-1340">
          <div className="relative" data-id="element-1341">
            <input type="text" placeholder="Search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full h-14 pl-12 pr-4 rounded-full border-2 border-[#6B4B3E] text-[#6B4B3E] text-lg focus:outline-none focus:border-[#D4A088]" data-id="element-1342" />
            <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#6B4B3E]" size={24} data-id="element-1343" />
          </div>
        </div>
        {/* FAQ Content */}
        <div className="flex-1 overflow-auto" data-id="element-1344">
          <div className="max-w-3xl mx-auto px-4 pb-16" data-id="element-1345">
            {filteredSections.map((section, index) => <div key={index} className="mb-12" data-id="element-1346">
                <h2 className="text-xl font-bold text-[#6B4B3E] mb-6" data-id="element-1347">
                  {section.title}
                </h2>
                <div className="space-y-4" data-id="element-1348">
                  {section.questions.map((faq, faqIndex) => <div key={faqIndex} className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1349">
                      <h3 className="text-lg font-medium text-[#6B4B3E] mb-3" data-id="element-1350">
                        {faq.question}
                      </h3>
                      <p className="text-[#6B4B3E]/80" data-id="element-1351">{faq.answer}</p>
                    </div>)}
                </div>
              </div>)}
          </div>
          {/* CTA Section */}
          <div className="bg-white py-16" data-id="element-1352">
            <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-1353">
              <h2 className="text-2xl font-bold text-[#6B4B3E] mb-4" data-id="element-1354">
                Still curious?
              </h2>
              <p className="text-lg text-[#6B4B3E]/80 mb-8" data-id="element-1355">
                The best way to learn is to try it.
              </p>
              <a href="#top" className="inline-flex items-center justify-center px-8 py-4 bg-[#D4A088] text-white rounded-full text-lg font-medium hover:bg-[#C28B73] transition-colors" data-id="element-1356">
                Click to Create
              </a>
            </div>
          </div>
        </div>
      </div>
    </>;
};